import { BackendValidationError } from './backend-errors';

/**
 * Marks an error whose message describes a malformed response rather than something the
 * backend said. Those messages are diagnostic English written for us, so a render site
 * that would otherwise show `err.message` to the user substitutes translated copy.
 */
export const UNREADABLE_RESPONSE_CODE = 'unreadable-response';

export function isUnreadableResponseError(error: unknown): boolean {
  return error instanceof BackendValidationError && error.code === UNREADABLE_RESPONSE_CODE;
}

/**
 * Read a list off a 200 body, refusing to treat an unreadable shape as an empty list.
 *
 * `body.things || []` turns a wrong-typed key into a value the caller then slices or
 * spreads, which throws during render — outside any try around the fetch, so no error
 * state catches it and the whole app blanks. Coercing to `[]` instead is worse in a
 * different way: it renders "nothing here" as a fact about the account, which a body we
 * could not read has not earned. Both cases are failures, so both throw.
 */
export function requireList<T>(body: unknown, key: string, message: string): T[] {
  const value = (body as Record<string, unknown> | null | undefined)?.[key];
  if (!Array.isArray(value)) {
    throw new BackendValidationError(message, UNREADABLE_RESPONSE_CODE);
  }
  return value as T[];
}

/**
 * The single-object counterpart of `requireList`, for the routes whose 200 carries one
 * resource under a singular key (`{"attachment": {...}}`, `{"request": {...}}`) rather
 * than a list. Same reasoning: a missing or wrong-typed key is a failure, and reporting
 * it as one beats handing `undefined` to a render that will throw on it later.
 */
export function requireObject<T>(body: unknown, key: string, message: string): T {
  const value = (body as Record<string, unknown> | null | undefined)?.[key];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new BackendValidationError(message, UNREADABLE_RESPONSE_CODE);
  }
  return value as T;
}
